import { apiDelete, apiGet, apiPatch, apiPost } from './client.js';

export type ServiceScopeType = 'per_model' | 'all_devices' | 'categories' | 'makes';
export type ResolveSource = 'model' | 'make' | 'category' | 'all';

export interface RepairService {
  id: string;
  name: string;
  categorySlug?: string | null;
  description?: string | null;
  defaultPrice?: string | null;
  defaultWarrantyDays?: number | null;
  defaultWarrantyType?: 'days' | 'lifetime' | 'none' | null;
  estimatedMinutes?: number | null;
  loyaltyOverride?: string | null;
  loyaltyEarnRate?: string | null;
  scopeType?: ServiceScopeType;
  scopeCategories?: string[];
  scopeMakeIds?: string[];
  scopeExcludedModelIds?: string[];
  flatPrice?: string | null;
  flatEstimatedMinutes?: number | null;
  flatWarrantyDays?: number | null;
  flatWarrantyType?: 'days' | 'lifetime' | 'none' | null;
  sortOrder?: number | null;
  displayOnPos?: boolean;
  displayOnPortal?: boolean;
  displayOnWidget?: boolean;
  active?: boolean;
  modelCount?: number;
  priceMin?: string | null;
  priceMax?: string | null;
  usageCount?: number;
  resolvedPrice?: string | null;
  hasPriceOverride?: boolean;
  resolvedEstimatedMinutes?: number | null;
  resolvedWarrantyDays?: number | null;
  resolvedWarrantyType?: 'days' | 'lifetime' | 'none' | null;
  price?: string | null;
  source?: ResolveSource;
  isExactMatch?: boolean;
}

export interface RepairServicePricing {
  id: string;
  serviceId: string;
  makeId?: string | null;
  makeName?: string | null;
  modelId?: string | null;
  modelName?: string | null;
  price: string;
  salePrice?: string | null;
  estimatedMinutes?: number | null;
  warrantyDays?: number | null;
  description?: string | null;
  active: boolean;
}

export interface RepairServiceSearchHit {
  serviceId: string;
  serviceName: string;
  makeName?: string | null;
  modelName?: string | null;
  price: string;
  salePrice?: string | null;
  estimatedMinutes?: number | null;
  warrantyDays?: number | null;
}

export interface ListRepairServicesParams {
  categorySlug?: string;
  makeId?: string;
  modelId?: string;
  category?: string;
  displayOnPos?: boolean;
  search?: string;
  active?: boolean;
  includeInactive?: boolean;
  requireModelPricing?: boolean;
  scopeType?: ServiceScopeType;
}

export function listRepairServices(params?: ListRepairServicesParams) {
  return apiGet<RepairService[]>('/api/v1/repair-services', { params }).then((res) => res.data);
}

export function searchRepairServices(q: string, limit = 10) {
  return apiGet<RepairServiceSearchHit[]>('/api/v1/repair-services/search', {
    params: { q, limit },
  }).then((res) => res.data);
}

export function createRepairService(body: Record<string, unknown>) {
  return apiPost<RepairService, Record<string, unknown>>('/api/v1/repair-services', body).then(
    (res) => res.data,
  );
}

export function updateRepairService(id: string, body: Record<string, unknown>) {
  return apiPatch<RepairService, Record<string, unknown>>(`/api/v1/repair-services/${id}`, body).then(
    (res) => res.data,
  );
}

export function previewRepairServiceScope(body: {
  scopeType: ServiceScopeType;
  scopeCategories?: string[];
  scopeMakeIds?: string[];
  scopeExcludedModelIds?: string[];
}) {
  return apiPost<{ matchingModelCount: number }, typeof body>(
    '/api/v1/repair-services/scope-preview',
    body,
  ).then((res) => res.data);
}

export function listScopeSuggestions() {
  return apiGet<
    Array<{
      serviceId: string;
      serviceName: string;
      category: string;
      flatPrice: string;
      modelCount: number;
    }>
  >('/api/v1/repair-services/scope-suggestions').then((res) => res.data);
}

export function bulkSetRepairServiceScope(body: {
  serviceIds: string[];
  scopeType: ServiceScopeType;
  scopeCategories?: string[];
  scopeMakeIds?: string[];
  scopeExcludedModelIds?: string[];
  flatPrice?: number | null;
  flatEstimatedMinutes?: number | null;
  flatWarrantyDays?: number | null;
  deactivatePricingRows?: boolean;
}) {
  return apiPost<{ count: number; serviceIds: string[] }, typeof body>(
    '/api/v1/repair-services/bulk-set-scope',
    body,
  ).then((res) => res.data);
}

export function listRepairServicePricing(serviceId: string) {
  return apiGet<RepairServicePricing[]>(`/api/v1/repair-services/${serviceId}/pricing`).then(
    (res) => res.data,
  );
}

export function upsertRepairServicePricing(
  serviceId: string,
  body: {
    makeId: string;
    modelId?: string;
    price: number | string;
    salePrice?: number | string;
    estimatedMinutes?: number;
    warrantyDays?: number;
    description?: string;
  },
) {
  return apiPost<RepairServicePricing, typeof body>(`/api/v1/repair-services/${serviceId}/pricing`, body).then(
    (res) => res.data,
  );
}

export function bulkUpsertRepairServicePricing(
  serviceId: string,
  body: {
    modelIds: string[];
    price: number;
    salePrice?: number;
    estimatedMinutes?: number;
    warrantyDays?: number;
  },
) {
  return apiPost<{ count: number }, typeof body>(
    `/api/v1/repair-services/${serviceId}/pricing/bulk`,
    body,
  ).then((res) => res.data);
}

export function deleteRepairServicePricing(serviceId: string, pricingId: string) {
  return apiDelete<void>(`/api/v1/repair-services/${serviceId}/pricing/${pricingId}`);
}

export const REPAIR_CATEGORIES = [
  { slug: 'screen', label: 'Screen' },
  { slug: 'battery', label: 'Battery' },
  { slug: 'charging', label: 'Charging' },
  { slug: 'camera', label: 'Camera' },
  { slug: 'audio', label: 'Audio' },
  { slug: 'software', label: 'Software' },
  { slug: 'data', label: 'Data' },
  { slug: 'other', label: 'Other' },
] as const;

export const DEVICE_SCOPE_CATEGORIES = [
  { value: 'phone', label: 'Phone' },
  { value: 'tablet', label: 'Tablet' },
  { value: 'laptop', label: 'Laptop' },
  { value: 'desktop', label: 'Desktop' },
  { value: 'console', label: 'Console' },
  { value: 'wearable', label: 'Wearable' },
  { value: 'other', label: 'Other' },
] as const;

export function formatServiceScope(service: RepairService): string {
  const scope = service.scopeType ?? 'per_model';
  if (scope === 'all_devices') return 'All devices';
  if (scope === 'categories') {
    const cats = service.scopeCategories ?? [];
    if (cats.length === 0) return 'Categories (none)';
    return cats.map((c) => c.charAt(0).toUpperCase() + c.slice(1)).join(',');
  }
  if (scope === 'makes') return 'Manufacturers';
  if (service.modelCount && service.modelCount > 0) return `${service.modelCount} models`;
  return 'Per model';
}
